/**
 * Route-aware asset prefetching.
 *
 * The background warm sweeps the whole `idle` phase — ~97 MB across 1000+ URLs — in manifest
 * order, which has no relationship to where the player is actually going. Open the kingdom page a
 * minute in and the sweep may not have reached its artwork yet, so you wait on a cold fetch even
 * though the app has been downloading continuously the whole time.
 *
 * This module reorders that work around intent: when a route is entered (or a link to it is
 * hovered), that page's assets are fetched immediately at high priority, ahead of the general
 * sweep. Same total bandwidth, spent on what is about to be looked at.
 *
 * Groups are matched against the URL path, so they stay correct as artwork is added, and an
 * unmatched asset simply stays in the background sweep.
 */

import { VERCEL_BLOB_PRELOAD_IDLE_URLS } from '../constants/vercelBlobPreloadUrls'
import { preloadBlobUrlsWithProgress } from './assetWarmup'

/** Path patterns that identify each destination's artwork. */
const ROUTE_ASSET_PATTERNS: Readonly<Record<string, readonly RegExp[]>> = {
  kingdom: [/\/palace\//i, /_merged_\/kingdom/i, /\/land\/era-details/i],
  worldmap: [/\/redesign\/world-map/i, /\/mvm\//i, /\/assets\/maps/i, /world-map/i],
  pve: [/\/pve\//i, /\/assets\/battle/i, /\/redesign\/barbarians/i],
  pvp: [/\/pvp\//i],
  alliance: [/\/land\/alliance/i, /\/redesign\/alliance/i],
  market: [/\/land\/market/i, /\/redesign\/shop/i, /\/shop\//i],
  espionage: [/\/land\/espionage/i],
  diplomacy: [/\/land\/diplomacy/i],
  inventory: [/\/redesign\/inventory/i, /craft_items|craft_weapons/i],
  minigame: [/onet-assets/i, /mini-game/i],
}

export type RouteAssetGroup = keyof typeof ROUTE_ASSET_PATTERNS

/** URL pathname → the location's route key, or null when a path maps to no artwork group. */
export function routeGroupForPath(pathname: string): RouteAssetGroup | null {
  const p = String(pathname || '').toLowerCase()
  if (p.includes('/palace') || p.includes('/kingdom')) return 'kingdom'
  if (p.includes('/world-map') || p.includes('/worldmap')) return 'worldmap'
  if (p.includes('/battlefield') || p.includes('/hex-battle') || p.includes('/pve')) return 'pve'
  if (p.includes('/pvp')) return 'pvp'
  if (p.includes('/alliance')) return 'alliance'
  if (p.includes('/market') || p.includes('/shop')) return 'market'
  if (p.includes('/espionage')) return 'espionage'
  if (p.includes('/diplomacy')) return 'diplomacy'
  if (p.includes('/inventory')) return 'inventory'
  if (p.includes('/eclipsera-bonds') || p.includes('/mini-game')) return 'minigame'
  return null
}

const urlsByGroup = new Map<RouteAssetGroup, string[]>()

function urlsFor(group: RouteAssetGroup): string[] {
  const cached = urlsByGroup.get(group)
  if (cached) return cached
  const patterns = ROUTE_ASSET_PATTERNS[group]
  const list = VERCEL_BLOB_PRELOAD_IDLE_URLS.filter((u) => {
    let path = u
    try {
      path = new URL(u).pathname
    } catch {
      /* relative URL — match the whole string */
    }
    return patterns.some((re) => re.test(path))
  })
  urlsByGroup.set(group, list)
  return list
}

const startedGroups = new Set<RouteAssetGroup>()

/**
 * Fetch a destination's artwork now, ahead of the general background sweep. Idempotent per group;
 * safe to call on every navigation and on every hover.
 *
 * Concurrency 6 (vs 3 for the idle sweep) because this is work the player is about to see, not
 * speculative filling.
 */
export function prefetchRouteAssets(group: RouteAssetGroup | null): void {
  if (!group || startedGroups.has(group)) return
  const urls = urlsFor(group)
  if (urls.length === 0) {
    startedGroups.add(group)
    return
  }
  startedGroups.add(group)
  void preloadBlobUrlsWithProgress(urls, () => {}, 6, `route:${group}`, {
    allowPartialComplete: true,
    // Bytes into Cache Storage is the goal; the browser decodes at paint time anyway.
    decodeImages: false,
  }).catch(() => {
    // A failed group should be retryable on the next visit.
    startedGroups.delete(group)
  })
}

/** Convenience for router integration: map a pathname and prefetch in one call. */
export function prefetchAssetsForPath(pathname: string): void {
  prefetchRouteAssets(routeGroupForPath(pathname))
}

/** Diagnostics: how the idle phase divides across destinations. */
export function routeAssetGroupSizes(): Record<string, number> {
  const out: Record<string, number> = {}
  for (const g of Object.keys(ROUTE_ASSET_PATTERNS) as RouteAssetGroup[]) out[g] = urlsFor(g).length
  return out
}
